import { InputValidity } from '@one/web-components';
import React, { useEffect, useRef, useState } from 'react';
import { AssistiveText, ExtendedValidationMessages } from '../form-elements';
import {
    BrowseButton,
    Container,
    InputWrapper,
    UploadAssistiveTextLayout,
    UploadOwcInput,
} from './input-file-uploader.style';

export type OnFileChangeType = {
    file: File | null;
    fileName: string;
};

interface InputFileUploaderProps {
    textButton: string;
    placeholder?: string;
    onFileChange?: (file: OnFileChangeType) => void;
    accept?: string;
    fileName?: string;
    disabled?: boolean;
    required?: boolean;
    className?: string;
    dataTestId?: string;
    validity?: InputValidity;
    validationMessages?: ExtendedValidationMessages;
}

export const InputFileUploader: React.FC<InputFileUploaderProps> = ({
    placeholder = 'No file selected',
    onFileChange,
    accept = '*',
    fileName: fileNameProp = '',
    disabled = false,
    required = false,
    textButton,
    className,
    dataTestId,
    validity,
    validationMessages,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [fileName, setFileName] = useState<string>(fileNameProp);

    const handleBrowseClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        fileInputRef.current?.click();
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        if (file) {
            setFileName(file.name);
            onFileChange?.({ file, fileName: file.name });
        }
    };
    const handleClearClick = () => {
        setFileName('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        onFileChange?.({ file: null, fileName: '' });
    };

    useEffect(() => {
        setFileName(fileNameProp);
    }, [fileNameProp]);

    return (
        <Container className={className}>
            <InputWrapper>
                <UploadOwcInput
                    value={fileName}
                    nativeInputProps={{ readOnly: true }}
                    placeholder={placeholder}
                    disabled={disabled}
                    required={required}
                    validity={validity}
                    data-testid={`${dataTestId}-input`}
                    onClearClick={handleClearClick}
                />
                <BrowseButton
                    variant={'secondary'}
                    onClick={handleBrowseClick}
                    disabled={disabled}
                    data-testid={`${dataTestId}-button`}
                >
                    {textButton}
                </BrowseButton>
            </InputWrapper>
            <input
                ref={fileInputRef}
                type="file"
                accept={accept}
                required={required}
                hidden
                data-testid={`${dataTestId}-file-input`}
                onChange={handleFileChange}
                style={{ display: 'none' }}
            />
            <UploadAssistiveTextLayout slot="assistive-text">
                <AssistiveText validity={validity} validationMessages={validationMessages} />
            </UploadAssistiveTextLayout>
        </Container>
    );
};
